import React, { useState, useEffect } from "react";
import { apiPost } from "../../api";
import { SvgIcon } from "../SvgIcon";
import { BtnSpinner } from "../BtnSpinner";

export function RecoveryEmailModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!props.isOpen) {
      setEmail("");
      setPassword("");
      setShowPassword(false);
      setSubmitting(false);
      setError(null);
      setSent(false);
    }
  }, [props.isOpen]);

  // Auto-close after success
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => props.onClose(), 3000);
    return () => clearTimeout(t);
  }, [sent, props.onClose]);

  async function handleSubmit() {
    if (!email.trim()) {
      setError("Please enter an email address.");
      return;
    }
    if (!password) {
      setError("Please enter your current password.");
      return;
    }

    setSubmitting(true);
    setError(null);

    // Verify password first
    const pwRes = await apiPost<{ ok: boolean }>("/v1/session/verify-password", { password });
    if (!pwRes.ok) {
      setSubmitting(false);
      setError("Incorrect password");
      return;
    }

    // Then submit recovery email
    const res = await apiPost<{ ok: boolean }>("/v1/account/recovery/email", { email: email.trim() });

    setSubmitting(false);

    if (!res.ok) {
      setError(res.error || "Failed to save recovery email.");
      return;
    }

    props.onSuccess?.(email.trim());
    setSent(true);
  }

  if (!props.isOpen) return null;

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard" style={{ width: "min(440px, 92vw)" }}>
        <div className="modalHeader">
          <div className="modalTitle">Recovery email</div>
          <button className="iconBtn" onClick={props.onClose} title="Close" disabled={submitting}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        <div className="modalBody" style={{ padding: "16px 20px" }}>
          {sent ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ display: "inline-flex", width: 28, height: 28, color: "var(--success, #10b981)", marginBottom: "12px" }}>
                <SvgIcon name="check-circle" title="Sent" />
              </div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text)", marginBottom: "8px" }}>
                Verification email sent
              </div>
              <div style={{ fontSize: "13px", color: "var(--muted)", lineHeight: "1.5" }}>
                Check your inbox at <strong>{email}</strong> and click the link to verify.
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5" }}>
                Add a recovery email address. A verification link will be sent to confirm ownership.
              </div>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                Email address
              </label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                placeholder="recovery@example.com"
                disabled={submitting}
                autoFocus
                style={{ width: "100%", boxSizing: "border-box" }}
              />

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px", marginTop: "16px" }}>
                Current password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  placeholder="Enter your password"
                  disabled={submitting}
                  style={{ width: "100%", boxSizing: "border-box", paddingRight: "36px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={submitting}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px",
                    color: "var(--muted)",
                    display: "inline-flex",
                    alignItems: "center",
                    width: 20,
                    height: 20
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <SvgIcon name={showPassword ? "eye-fill" : "eye"} title={showPassword ? "Hide" : "Show"} />
                </button>
              </div>

              {error && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "10px" }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {!sent && (
          <div className="modalFooter" style={{ padding: "12px 20px" }}>
            <button className="btn" onClick={props.onClose} disabled={submitting}>
              Cancel
            </button>
            <div style={{ flex: 1 }} />
            <button
              className="btn primary"
              onClick={handleSubmit}
              disabled={submitting || !email.trim() || !password}
            >
              <BtnSpinner loading={submitting}>{submitting ? "Sending…" : "Send verification"}</BtnSpinner>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
